import React, { useState, useEffect } from 'react';
import { Modal, Input, Select, Button, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import axios from '../../../utility/axios';
import '../../../styles/settings/settings-common.css';
import '../../../styles/settings/modals.css';

const { Option } = Select;

const AddUserModal = ({ visible, onClose, onUserCreated, onUserUpdated, roles = [], editingUser = null }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        firstname: '',
        lastname: '',
        role: null,
        status: 'Active'
    });

    const isEditMode = !!editingUser;

    useEffect(() => {
        if (visible && roles.length > 0) {
            if (editingUser) {
                const userRole = roles.find(r => (r.name || '').toLowerCase() === (editingUser.role || '').toLowerCase());
                setFormData({
                    email: editingUser.email,
                    firstname: editingUser.firstname || '',
                    lastname: editingUser.lastname || '',
                    role: userRole ? userRole.id : roles[0]?.id,
                    status: editingUser.status || 'Active'
                });
            } else {
                const userRole = roles.find(r => (r.name || '').toLowerCase() === 'user');
                const defaultRole = userRole ? userRole.id : roles[0].id;
                setFormData(prev => ({
                    ...prev,
                    role: defaultRole
                }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, roles, editingUser]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        if (!formData.firstname) {
            message.error(t('admin.userManagement.addUser.firstNameRequired'));
            return;
        }
        if (!formData.lastname) {
            message.error(t('admin.userManagement.addUser.lastNameRequired'));
            return;
        }
        if (!formData.role) {
            message.error(t('admin.userManagement.addUser.roleRequired'));
            return;
        }
        if (!isEditMode && !formData.email) {
            message.error(t('admin.userManagement.addUser.emailRequired'));
            return;
        }

        setLoading(true);
        try {
            if (isEditMode) {
                await axios.post('/api/admin/users/update/', {
                    unique_identifier: editingUser.unique_identifier,
                    firstname: formData.firstname,
                    lastname: formData.lastname,
                    status: formData.status,
                    role_id: formData.role
                });
                message.success(t('admin.userManagement.userUpdated'));
                if (onUserUpdated) onUserUpdated();
            } else {
                const payload = {
                    email: formData.email,
                    firstname: formData.firstname,
                    lastname: formData.lastname,
                    role_id: formData.role,
                    status: formData.status
                };
                const response = await axios.post('/api/admin/create-user/', payload);
                message.success(t('admin.userManagement.addUser.userCreated'));
                if (onUserCreated) onUserCreated(response.data);
            }
            const userRole = roles.find(r => (r.name || '').toLowerCase() === 'user');
            const defaultRole = roles.length > 0 ? (userRole ? userRole.id : roles[0].id) : null;
            setFormData({
                email: '',
                firstname: '',
                lastname: '',
                role: defaultRole,
                status: 'Active'
            });
            onClose();
        } catch (error) {
            console.error(isEditMode ? 'Error updating user:' : 'Error creating user:', error);
            message.error(error.response?.data?.error || (isEditMode ? t('admin.userManagement.failedUpdate') : t('admin.userManagement.addUser.failedToCreate')));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        const userRole = roles.find(r => (r.name || '').toLowerCase() === 'user');
        const defaultRole = roles.length > 0 ? (userRole ? userRole.id : roles[0].id) : null;
        setFormData({
            email: '',
            firstname: '',
            lastname: '',
            role: defaultRole,
            status: 'Active'
        });
        onClose();
    };

    return (
        <Modal
            open={visible}
            onCancel={handleCancel}
            footer={null}
            title={isEditMode ? t('admin.userManagement.editUser.title') : t('admin.userManagement.addUser.title')}
            closeIcon={<CloseOutlined />}
            width={560}
            className="settings-modal settings-modal--user contact-add-modal"
            centered
        >
            <div className="contact-add-modal-content">
                <div className="contact-add-form">
                    <div className="contact-add-fields">
                        <Input
                            placeholder={t('admin.userManagement.addUser.emailPlaceholder')}
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className="contact-add-input"
                            type="email"
                            disabled={isEditMode}
                        />
                        <div className="contact-add-form-row">
                            <Input
                                placeholder={t('admin.userManagement.addUser.firstNamePlaceholder')}
                                value={formData.firstname}
                                onChange={(e) => handleInputChange('firstname', e.target.value)}
                                className="contact-add-input"
                            />
                            <Input
                                placeholder={t('admin.userManagement.addUser.lastNamePlaceholder')}
                                value={formData.lastname}
                                onChange={(e) => handleInputChange('lastname', e.target.value)}
                                className="contact-add-input"
                            />
                        </div>
                        <Select
                            value={formData.role}
                            onChange={(value) => handleInputChange('role', value)}
                            placeholder={t('admin.userManagement.addUser.role')}
                            className="add-user-select"
                            style={{ width: '100%' }}
                        >
                            {roles.map(role => (
                                <Option key={role.id} value={role.id}>
                                    {role.name}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            value={formData.status}
                            onChange={(value) => handleInputChange('status', value)}
                            placeholder={t('admin.userManagement.status')}
                            className="add-user-select"
                            style={{ width: '100%' }}
                        >
                            <Option value="Active">{t('admin.userManagement.active')}</Option>
                            <Option value="Inactive">{t('admin.userManagement.inactive')}</Option>
                            <Option value="Disabled">{t('admin.userManagement.disabled')}</Option>
                            <Option value="Pending">{t('admin.userManagement.pending')}</Option>
                        </Select>
                    </div>
                </div>

                <div className="contact-add-modal-footer">
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={loading}
                        className="contact-add-create-btn"
                    >
                        {isEditMode ? t('admin.userManagement.editUser.save') : t('admin.userManagement.addUser.create')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AddUserModal;

